/**
 * Geometric Utility Functions
 * 
 * This file provides utility functions for geometric calculations used in the application.
 */
import { isArray, max, round } from "lodash";
import {
  RectangleParams,
  Rectangle,
  Point,
  Device,
  DeviceLocation,
  Quantity,
  Spacing,
  Coverage,
  Table,
  OverlapResult,
  CollisionResult,
  NumberArray
} from "../types/utilities/geometric";

/**
 * Given a bounding box, returns the rectangle coordinates as [x0, y0, x1, y1]
 * 
 * @param box - The rectangle parameters
 * @returns A rectangle represented as [x0, y0, x1, y1]
 */
export const getRectangle = (box: RectangleParams): Rectangle => {
  const { x0, y0, x1, y1, right, bottom, left = 0, top = 0, length = 0, width = 0 } = box;
  return [
    x0 ?? left, 
    y0 ?? top, 
    x1 ?? right ?? (left + length), 
    y1 ?? bottom ?? (top + width)
  ];
};

/**
 * Check if a coordinate pair [x,y] is inside a rectangle
 * 
 * @param test - The point to test
 * @param box - The rectangle to check against
 * @returns True if the point is inside the rectangle, false otherwise
 */
export const isInside = (test: Point | NumberArray = [0, 0], box: Rectangle | RectangleParams | NumberArray): boolean => {
  const rect: Rectangle = isArray(box) ? box as Rectangle : getRectangle(box as RectangleParams);
  return test.reduce((acc, val, index) => {
    const [min, max] = index % 2 === 0 ? [rect[0], rect[2]] : [rect[1], rect[3]];
    return acc && val >= min && val <= max;
  }, true);
};

/**
 * Given an array of 4 points [x0. y0. x1, y1], returns the area
 * 
 * @param points - The rectangle points
 * @returns The surface area of the rectangle
 */
export const getSurface = (points: Rectangle | NumberArray): number => {
  // Ensure we have at least 4 elements to work with
  if (points.length < 4) {
    console.warn('getSurface called with insufficient points, expected at least 4 elements');
    return 0;
  }
  return Math.abs(points[0] - points[2]) * Math.abs(points[1] - points[3]);
};

/**
 * Speakers-per-axis count for a coverage span. Rounds the span to the nearest whole number of
 * `spacing` (centre-to-centre pitch) intervals, subtracts the box's `spkrAdj`, then trims one more
 * unit when the leftover edge margin is too small to justify it. Shared by the primary speaker grid
 * and the bar-expansion speaker grid so both count the same way for the same span.
 *
 * @param dim - The coverage span along the axis (length or width)
 * @param spacing - Centre-to-centre speaker pitch along the axis
 * @param spkrAdj - Coverage-box adjustment subtracted from the raw count (0 in full-room)
 * @returns The whole number of speakers along the axis (minimum 1)
 */
export const getSpeakerAxisCount = (dim: number, spacing: number, spkrAdj = 0): number => {
  if (!(spacing > 0)) return 1;
  const count = max([1, round(dim / spacing) - spkrAdj]) as number;
  if (count === 1) return 1;
  const edgeToFirstSpeaker = dim / 2 - (count / 2) * spacing + spacing / 2;
  return edgeToFirstSpeaker >= spacing / 2 ? count : count - 1;
};

/**
 * Calculate overlaps and jump points. Given an area and the radius of a circle,
 * it returns a list of quantity changes and the overlap for those changes
 * 
 * @param rect - The rectangle dimensions
 * @param radius - The radius of the coverage circle
 * @param coverage - Coverage options
 * @param table - Table information
 * @param isEndTable - Whether this is an end table
 * @returns The jump points and overlaps for device placement
 */
export const getOverlaps = (
  rect: RectangleParams = { length: 0, width: 0 }, 
  radius: number, 
  coverage: Coverage = { full_room: false, focused: true }, 
  table: Table = {}, 
  isEndTable = false
): OverlapResult => {
  const overlaps: number[] = [];
  const jumpPoints: number[] = [];
  const optimizedPoints = new Set<number>(); // Track unique optimized quantities
  let currentValue = 0;

  const MIN_OVERLAP = 1.4142; // no-gap spacing
  const MAX_OVERLAP = 2; // center to center overlap
  const OVERLAP_STEP = 0.01;
  const { length = 0, width = 0 } = rect;
  
  for (let ov = MIN_OVERLAP; ov <= MAX_OVERLAP; ov += OVERLAP_STEP) {
    const xQty = max([1, round(length / radius / ov)]) as number;
    const yQty = max([1, round(width / radius / ov)]) as number;
    const qty = xQty * yQty;

    if (qty !== currentValue) {
      currentValue = qty;

      // Optimize
      // Calculate how many mics would remain after optimization
      let optimizedQty = qty;
      if (table?.shape && coverage.focused && xQty >= 2 && yQty >= 3) {
        // Basic rectangular optimization
        const edgeMics = 2 * (xQty + yQty - 2); // Mics around the perimeter
        optimizedQty = edgeMics;

        // End table optimization
        if (isEndTable) {
          optimizedQty -= yQty - 2;
        }
      }

      // Only add unique quantities
      if (!optimizedPoints.has(optimizedQty)) {
        jumpPoints.push(optimizedQty);
        overlaps.push(round(ov, 4));
        optimizedPoints.add(optimizedQty);
      }
    }
  }

  return {
    jumpPoints: jumpPoints.reverse(),
    overlaps: overlaps.reverse()
  };
};

/**
 * Calculate device coordinates given coverage box, x&y quantities, x&y spacing
 * Returns an array with the coordinates of every device
 * 
 * @param model - The device model
 * @param modelName - The model name (defaults to model if not provided)
 * @param coverageBox - The coverage box dimensions
 * @param qty - The quantity of devices in x and y dimensions
 * @param spacing - The spacing between devices in x and y dimensions
 * @returns Array of devices with their model and location
 */
export const calculateCoordinates = (
  model: string,
  modelName: string | null = null,
  coverageBox: RectangleParams,
  qty: Quantity,
  spacing: Spacing,
  height?: number
): Device[] => {
  // note: there might be an issue here. Old tool placed end tables against the wall so the first speaker was adjusted a bit
  const { left = 0, top = 0, length = 0, width = 0 } = coverageBox;

  const startX = left + (length - (qty.x - 1) * spacing.x) / 2;
  const startY = top + (width - (qty.y - 1) * spacing.y) / 2;

  const devArray: Device[] = Array.from({ length: qty.x * qty.y }, (_, index) => {
    const x = startX + (index % qty.x) * spacing.x;
    const y = startY + Math.floor(index / qty.x) * spacing.y;
    const location: DeviceLocation = height !== undefined ? { x, y, z: height } : { x, y };

    return {
      model,
      modelName: modelName ?? model,
      location
    };
  });

  return devArray;
};

/**
 * Calculate the distance between two points
 * 
 * @param point1 - The first point
 * @param point2 - The second point
 * @returns The distance between the two points
 */
export const getDistanceBetween = (point1: DeviceLocation, point2: DeviceLocation): number => {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.hypot(dx, dy);
};

/**
 * Calculate collisions between two arrays of points and return number of collisions
 * and the points that are colliding
 * 
 * @param micArray - Array of microphone devices
 * @param spkrArray - Array of speaker devices
 * @returns Collision results with count and coordinates
 */
export const getCollisions = (micArray: Device[], spkrArray: Device[]): CollisionResult => {
  let collisions = 0;
  const centerPoints: { microphones: DeviceLocation[], speakers: DeviceLocation[] } = {
    microphones: [],
    speakers: []
  };

  for (const mic of micArray) {
    for (const spkr of spkrArray) {
      const distance = getDistanceBetween(mic.location, spkr.location);
      // 0.86 is the minimum distance between a mic and a speaker
      // corresponds to the center of two diagonal 2x2 ceiling tiles
      if (distance < 0.86) {
        collisions++;
        centerPoints.microphones.push({...mic.location});
        centerPoints.speakers.push({...spkr.location});
      }
    }
  }

  return { collisions, centerPoints };
};

/**
 * Evenly sample `count` indices (by position) out of `length` items, always keeping the first
 * and last.
 */
export const sampleEvenlySpreadIndices = (length: number, count: number): number[] => {
  if (count <= 0 || length <= 0) return [];
  if (count >= length) return Array.from({ length }, (_, i) => i);
  if (count === 1) return [0];
  return Array.from({ length: count }, (_, i) => Math.round((i * (length - 1)) / (count - 1)));
};

/**
 * Sample `count` indices out of `length` evenly-spaced candidates by dividing the FULL range
 * into `count` equal segments and picking the candidate nearest each segment's centre — the
 * standard "stratified sampling" approach for minimising the worst gap between picks.
 *
 * Deliberately different from `sampleEvenlySpreadIndices` (which always keeps index 0 and
 * length-1): for a small reduction (e.g. 3 candidates → 2) the segment centres still fall
 * closest to the two extremes, so behaviour matches. But for a LARGE reduction (e.g. 6
 * candidates → 2 — a dense grid from a small mic radius at Poor acoustics), always keeping the
 * literal extremes leaves one huge gap in the middle; centred segments instead spread the picks
 * across the full range, e.g. 6→2 picks indices 1 and 4 rather than 0 and 5, roughly quartering
 * the range instead of concentrating the whole span between two edge picks.
 *
 * Only the FIRST HALF of the picks is computed from the segment-centre formula; the second half
 * is its mirror image (`length - 1 - idx`), and an odd `count`'s middle pick is the array's exact
 * centre. This is deliberate, not just tidier code: computing every pick independently via
 * `floor(((i + 0.5) * length) / count)` is only symmetric by ACCIDENT of how the fractional part
 * happens to round away for a given (length, count) pair — it works for 3→2 ([0, 2]) and 6→2
 * ([1, 4]), but for 4→2 the two segment centres land on EXACT integers (1.0 and 3.0) with no
 * fractional part to round away, so `floor` returns them unchanged as `[1, 3]` — which averages to
 * index 2.0, not the true centre 1.5, silently shifting an even 4-row grid capped to 2 rows off
 * true centre (e.g. an 80'×50' bar+expansion room with a plain 4×4 ceiling-mic grid capped to
 * 2×2). Mirroring the first half guarantees `idx[k] + idx[count-1-k] === length-1` by
 * construction, so the result is symmetric for every (length, count) pair, not just the ones
 * where the rounding happens to cancel out.
 */
export const sampleGridCenteredIndices = (length: number, count: number): number[] => {
  if (count <= 0 || length <= 0) return [];
  if (count >= length) return Array.from({ length }, (_, i) => i);
  const idxs = new Array<number>(count);
  const half = Math.floor(count / 2);
  for (let i = 0; i < half; i++) {
    const idx = Math.min(length - 1, Math.floor(((i + 0.5) * length) / count));
    idxs[i] = idx;
    idxs[count - 1 - i] = length - 1 - idx;
  }
  if (count % 2 === 1) {
    idxs[half] = Math.round((length - 1) / 2);
  }
  return [...new Set(idxs)].sort((a, b) => a - b);
};

/**
 * Column tolerance, in metres, for `sortDevicesByColumnThenRow`.
 *
 * Two devices are "in the same column" when their x differs by no more than this. It has to clear
 * two thresholds:
 *   - float noise. Mirrored pairs (a keystone/vshape path samples top and bottom edges at mirrored
 *     arc positions) are mathematically at the SAME x, but `getPointAtLength` + the px→room
 *     transform leave a ~1e-12 residue. A raw `a.x - b.x` comparator therefore returns non-zero and
 *     decides the order by noise, which is what reversed each pair's y ordering in the mic
 *     locations table.
 *   - display rounding. `distanceToString` renders metric to 0.1 m and imperial to the nearest inch,
 *     so devices whose X reads identically in the table can differ by up to 0.1 m in state. Anything
 *     the user sees as one column must be ordered by Y, so the tolerance is set at that 0.1 m.
 *
 * Safe against merging genuinely distinct columns: expansion mic column pitch is
 * `defTableOverlap (1.34) × coverageRadius`, and the smallest radius in the catalog is 1.0 m
 * (ttm_x at Poor), so real columns are >= ~1.3 m apart — an order of magnitude clear of this.
 */
export const DEVICE_COLUMN_TOLERANCE_M = 0.1;

/**
 * Orders devices for position listings: by column (x ascending), then top-to-bottom within each
 * column (y ascending).
 *
 * Clusters first, then sorts, rather than using an epsilon comparator — an epsilon comparator is
 * not transitive, so `Array.prototype.sort` can produce different results for the same set
 * depending on the input order and the engine's sort implementation. Clustering is deterministic.
 * Each cluster is anchored on its own first x (not the running previous x) so a long run of
 * near-neighbours can't chain into one giant column.
 *
 * Used by the Equipment list's Microphone locations tab and the PDF's microphone positioning detail
 * so both number expansion mics identically at every acoustic tier, regardless of the traversal
 * order the placement algorithm happened to emit.
 */
export const sortDevicesByColumnThenRow = <T extends { location?: { x?: number; y?: number } }>(
  items: T[],
  tolerance: number = DEVICE_COLUMN_TOLERANCE_M,
): T[] => {
  if (items.length < 2) return [...items];
  const x = (d: T) => d?.location?.x ?? 0;
  const y = (d: T) => d?.location?.y ?? 0;

  const byX = [...items].sort((a, b) => x(a) - x(b));

  const columns: T[][] = [];
  let anchorX = Number.NaN;
  for (const item of byX) {
    if (columns.length === 0 || Math.abs(x(item) - anchorX) > tolerance) {
      columns.push([item]);
      anchorX = x(item);
    } else {
      columns[columns.length - 1].push(item);
    }
  }

  return columns.flatMap((col) => col.sort((a, b) => y(a) - y(b)));
};

/**
 * Caps an auto-placed ceiling-expansion-mic layout down to `count` mics, preserving the grid's
 * row/column symmetry instead of flattening it into one sorted list and sampling individual
 * points. `calculateMicPlacement` lays ceiling expansion mics out as a column × row grid that's
 * mirrored top/bottom around the box centre (e.g. 3 columns × 2 rows for a keystone table, or a
 * dense 6×6 grid at Poor acoustics where the smaller mic radius needs many more tiles) — a plain
 * even-index sample over the flattened, x-then-y-sorted array picks points from whichever row
 * each sampled index happens to land in, which for an odd number of columns almost always grabs
 * 2 points from one row and 2 from the other instead of a symmetric pair from each row.
 *
 * For a uniform grid (more than one column AND more than one row), picks a near-square
 * colCount × rowCount sub-grid (same `round(sqrt(count))` convention `calcBarExpansionPlacement`
 * in logic.js already uses for this exact "cap a grid to a budget" problem) and evenly samples
 * that many columns and that many row-positions — e.g. a 6×6 grid capped to 4 keeps the 2×2
 * sub-grid at the four corners, rather than "at least one whole column" (which, for a column
 * taller than the cap itself, returned MORE mics than the cap — e.g. clamping to 1 whole 6-row
 * column when the budget was only 4).
 *
 * Everything else (a single row, or an irregular count per column from a perimeter-path
 * placement) goes through a COLUMN-AWARE fallback that keeps whole columns together, so a
 * mirrored pair on a two-arm table survives the trim rather than being split. Two invariants
 * hold on every branch:
 *   - the result never contains more than `count` items, and never FEWER than
 *     `min(count, items.length)`;
 *   - a column is taken whole or not at all (the fallback), so a pair is never half-kept while
 *     the budget still had room for both.
 *
 * Shared between `reduxModules/ducks/room.ts` (setExpansionMicLayout — the authoritative,
 * port-budget-capped device layout) and `reduxModules/calculations/roomCalculations.ts` (the
 * heatmap's own input, built from the same pre-cap coords in the same pass, one dispatch earlier
 * than `setExpansionMicLayout` runs) so both always agree on exactly which mics survive the cap —
 * otherwise the heatmap can show/omit coverage for a mic that isn't/is actually placed.
 */
export const capExpansionGridLayout = <T extends { location?: { x?: number; y?: number } }>(
  items: T[],
  count: number,
): T[] => {
  if (count <= 0) return [];
  if (count >= items.length) return items;

  const xOf = (d: T) => d?.location?.x ?? 0;
  const yOf = (d: T) => d?.location?.y ?? 0;

  // Cluster on the SHARED display tolerance, not a 1e-3 epsilon. A mirrored pair is only
  // nominally at the same x: on a ceiling grid the residue is float noise (~1e-12), but
  // Scenario 3's reduced-arc TABLE placement leaves a real ~12 mm asymmetry — 12x a 1e-3
  // epsilon — so every pair split into two single-item columns and the structure this function
  // exists to preserve was destroyed before any sampling ran. Real columns are >= ~1.3 m apart
  // (see DEVICE_COLUMN_TOLERANCE_M), an order of magnitude clear of 0.1 m, so widening the
  // tolerance cannot merge distinct ones. Sorted by x first so clustering is order-independent.
  type Column = { x: number; items: T[] };
  const columns: Column[] = [];
  for (const item of [...items].sort((a, b) => xOf(a) - xOf(b))) {
    const last = columns[columns.length - 1];
    if (last && Math.abs(last.x - xOf(item)) <= DEVICE_COLUMN_TOLERANCE_M) last.items.push(item);
    else columns.push({ x: xOf(item), items: [item] });
  }
  columns.forEach((c) => c.items.sort((a, b) => yOf(a) - yOf(b)));

  // Never return fewer mics than the budget allows. A selection can legitimately land short —
  // a sub-grid whose dimensions don't divide evenly, or a column too tall to take whole — and
  // the old code accepted any short result (`picked.length <= count`). That is how a 3-column
  // x 1-row line capped to 2 returned ONE mic. Top up from whatever wasn't chosen.
  const topUp = (chosen: T[]): T[] => {
    const out = chosen.filter((item, i) => chosen.indexOf(item) === i);
    if (out.length >= count) return out;
    const taken = new Set(out);
    const rest = columns.flatMap((c) => c.items).filter((item) => !taken.has(item));
    const need = Math.min(count - out.length, rest.length);
    for (const i of sampleGridCenteredIndices(rest.length, need)) out.push(rest[i]);
    return out;
  };

  const rowsPerCol = columns[0]?.items.length ?? 0;
  // `rowsPerCol > 1`: a single ROW of columns is a line, not a grid. It has no row structure to
  // preserve, and the near-square maths below degenerates on it — 3 columns x 1 row capped to 2
  // gives colCount = round(sqrt(2)) = 1 and rowCount = min(1, 2) = 1, i.e. ONE mic against a
  // budget of two. Lines belong in the column-aware fallback.
  const isUniformGrid = columns.length > 1 && rowsPerCol > 1
    && columns.every((c) => c.items.length === rowsPerCol);

  if (isUniformGrid) {
    const colCount = Math.max(1, Math.min(columns.length, Math.round(Math.sqrt(count))));
    const rowCount = Math.max(1, Math.min(rowsPerCol, Math.round(count / colCount)));
    // sampleGridCenteredIndices (not sampleEvenlySpreadIndices): for a grid much denser than the
    // budget (e.g. 6 columns capped to 2), always keeping the literal edge columns leaves one
    // huge gap in the middle of the coverage box. Centred-segment sampling spreads the kept
    // columns/rows across the FULL range instead — see its own doc comment.
    const colIdxs = sampleGridCenteredIndices(columns.length, colCount);
    const rowIdxs = sampleGridCenteredIndices(rowsPerCol, rowCount);
    const picked = colIdxs.flatMap((ci) => rowIdxs.map((ri) => columns[ci].items[ri]));
    // colCount*rowCount can round up past count (e.g. count=3 → colCount=round(sqrt(3))=2,
    // rowCount=round(3/2)=2 → 4 picks) — trim the corner-most picks down to the exact budget
    // rather than silently exceeding it.
    if (picked.length > count) return sampleEvenlySpreadIndices(picked.length, count).map((i) => picked[i]);
    return topUp(picked);
  }

  // Column-aware fallback (irregular column sizes, or a single row). Whole columns are kept
  // together so a mirrored pair on a two-arm table survives the trim intact. The old flat index
  // sample discarded column structure entirely and routinely split pairs: a keystone Scenario 3
  // emitting [upper-arm, lower-arm, apex] capped to 2 went through
  // sampleEvenlySpreadIndices(3, 2) = "first and last" = one arm + the apex, leaving one arm of
  // the V micced and the other bare.
  const sameSize = columns.every((c) => c.items.length === rowsPerCol);
  const order = sameSize
    // Nothing to discriminate on (a single row, or one column): keep the existing
    // spread-across-x behaviour by taking centred column positions.
    ? sampleGridCenteredIndices(columns.length, Math.min(columns.length, count))
    // Mixed sizes: prefer the columns holding the most mics, so a mirrored pair beats a lone
    // apex when the budget only fits one column. Ties break toward the middle of the x range.
    : columns.map((_, i) => i).sort((a, b) => (
      columns[b].items.length - columns[a].items.length
      || Math.abs(a - (columns.length - 1) / 2) - Math.abs(b - (columns.length - 1) / 2)
    ));

  const picked: T[] = [];
  for (const i of order) {
    // Skip a column that would overflow the budget rather than taking part of it — a partial
    // column is exactly the lopsided half-pair this branch exists to avoid. `topUp` fills any
    // remainder afterwards.
    if (picked.length + columns[i].items.length > count) continue;
    picked.push(...columns[i].items);
  }
  return topUp(picked);
};
